import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';
import { PeopleBlockBridge, EntityRow, mergeEntityRows } from './people-block-bridge';

/**
 * REMOVE, REINSTATE, ERASE, TRANSFER — the four people blocks (Plan 3, 2026-10-03).
 *
 * Value blocks returning 'success', 'cancelled' or an error message, like "add user". A person is named
 * by RECORD ID: erased and username-only people have no email. Each compiles to
 * `await this.peopleActions.<verb>(…)` — PeopleActionsService on the running page, which shows the
 * confirm overlay and then calls managePeople.
 *
 * NOTHING HERE IMPORTS ANGULAR. This file is compiled into olympus-functions' drydock bundle.
 *
 * NOT IN THE AI'S VOCABULARY (Plan 4). They round-trip through blockly-to-steps / steps-to-blockly so an
 * AI rebuild keeps one a builder placed; the AI does not introduce them.
 */
export const PEOPLE_BLOCK_TYPES = ['person_remove', 'person_reinstate', 'person_erase', 'person_transfer'] as const;

const COLOUR = '#2e7d32';       // the user blocks' colour (special-functions-helper.ts)
const ERASE_COLOUR = '#c62828'; // erase stands apart in the palette too
const HEX = /^#[0-9a-fA-F]{6}$/;
export const THEME_DEFAULTS: Record<'ACCENT' | 'SURFACE' | 'TEXT', string> = { ACCENT: '#2563eb', SURFACE: '#ffffff', TEXT: '#111827' };

/** Three colour knobs (owner, 2026-10-03). No colour picker exists here, so a colour is typed as #RRGGBB;
 *  anything else is refused and the field keeps its value. Erase's red is not a knob. */
function appendTheme(block: any): void {
  const hex = (v: string) => (HEX.test(String(v)) ? String(v) : null);
  block.appendDummyInput('THEME_LABEL').appendField('overlay colours');
  for (const [name, label] of [['ACCENT', 'accent'], ['SURFACE', 'background'], ['TEXT', 'text']] as const) {
    block.appendDummyInput(`THEME_${name}`).appendField(label)
      .appendField(new (Blockly as any).FieldTextInput(THEME_DEFAULTS[name], hex), name);
  }
}

export function themeCode(block: any): string {
  return JSON.stringify({ accent: block.getFieldValue('ACCENT'), surface: block.getFieldValue('SURFACE'), text: block.getFieldValue('TEXT') });
}

const value = (block: any, name: string) =>
  (javascriptGenerator as any).valueToCode(block, name, (javascriptGenerator as any).ORDER_ATOMIC) || "''";

function personBlock(type: string, title: string, method: string, colour: string, tooltip: string): void {
  (Blockly as any).Blocks[type] = {
    init: function () {
      this.appendDummyInput().appendField(title);
      this.appendValueInput('PERSON').setCheck('String').appendField('person id');
      appendTheme(this);
      this.setInputsInline(false);
      this.setOutput(true, 'String');
      this.setColour(colour);
      this.setTooltip(tooltip);
      this.setHelpUrl('');
    },
  };
  (javascriptGenerator as any).forBlock[type] = function (block: any) {
    return [`await this.peopleActions.${method}(${value(block, 'PERSON')}, ${themeCode(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

function registerTransferBlock(): void {
  (Blockly as any).Blocks['person_transfer'] = {
    init: function () {
      this.appendDummyInput().appendField('transfer work');
      this.appendValueInput('FROM').setCheck('String').appendField('from person id');
      this.appendValueInput('TO').setCheck('String').appendField('to person id');
      this.appendDummyInput('ENTITIES_LABEL').appendField('records to move');
      // A fresh block offers every entity that points at people, all ticked (owner, 2026-10-03).
      this.setEntities_(PeopleBlockBridge.referencingEntities.map((e) => ({ ...e, on: true })));
      appendTheme(this);
      this.setInputsInline(false);
      this.setOutput(true, 'String');
      this.setColour(COLOUR);
      this.setTooltip(
        "Admin only. Hands every record that points at one person to another — in the ticked kinds of record only — " +
        "after the admin confirms. Nothing is copied or deleted. Work can come from anyone, even someone erased, " +
        "but only go to someone still in the workspace. Returns 'success', 'cancelled' or an error.");
      this.setHelpUrl('');
    },
    /** The rows ARE the choice; the checkboxes only show it. Rebuilt in place, before the colour rows. */
    setEntities_: function (rows: EntityRow[]) {
      for (const inp of this.inputList.slice()) if (inp.name && inp.name.indexOf('ENT_') === 0) this.removeInput(inp.name);
      this.entityRows_ = rows;
      for (const r of rows) {
        const field = new (Blockly as any).FieldCheckbox(r.on ? 'TRUE' : 'FALSE', (val: string) => { r.on = val === 'TRUE'; return val; });
        this.appendDummyInput('ENT_' + r.id).appendField(field, 'ENTCHK_' + r.id).appendField(r.name);
        if (this.getInput('THEME_LABEL')) this.moveInputBefore('ENT_' + r.id, 'THEME_LABEL');
      }
    },
    chosenEntities_: function (): string[] {
      return (this.entityRows_ || []).filter((r: EntityRow) => r.on).map((r: EntityRow) => r.id);
    },
    /** Tick exactly these ids — used when steps rebuild the block. Same rule as mergeEntityRows: with a
     *  known list (the editor — PeopleBlockBridge.referencingEntities non-empty), an id the list does not
     *  know is DROPPED, because the server would refuse the whole transfer over it. Headless (the bridge
     *  empty), every id is kept — there is nothing to check it against. */
    setChosenEntities_: function (ids: string[]) {
      const want = new Set(ids);
      const known = PeopleBlockBridge.referencingEntities.length > 0;
      const rows: EntityRow[] = (this.entityRows_ || []).map((r: EntityRow) => ({ ...r, on: want.has(r.id) }));
      if (!known) for (const id of ids) if (!rows.some((r) => r.id === id)) rows.push({ id, name: id, on: true });
      this.setEntities_(rows);
    },
    mutationToDom: function () {
      const m = (Blockly as any).utils.xml.createElement('mutation');
      for (const r of this.entityRows_ || []) {
        const e = (Blockly as any).utils.xml.createElement('e');
        e.setAttribute('id', r.id);
        e.setAttribute('name', r.name);
        e.setAttribute('on', r.on ? '1' : '0');
        m.appendChild(e);
      }
      return m;
    },
    domToMutation: function (xml: any) {
      const saved: EntityRow[] = [];
      const es = xml && xml.getElementsByTagName ? xml.getElementsByTagName('e') : [];
      for (let i = 0; i < es.length; i++) {
        const id = es[i].getAttribute('id');
        if (id) saved.push({ id, name: es[i].getAttribute('name') || id, on: es[i].getAttribute('on') === '1' });
      }
      this.setEntities_(mergeEntityRows(saved, PeopleBlockBridge.referencingEntities));
    },
  };
  (javascriptGenerator as any).forBlock['person_transfer'] = function (block: any) {
    return [
      `await this.peopleActions.transfer(${value(block, 'FROM')}, ${value(block, 'TO')}, ${JSON.stringify(block.chosenEntities_())}, ${themeCode(block)})`,
      (javascriptGenerator as any).ORDER_ATOMIC,
    ];
  };
}

export function registerPeopleBlocks(): void {
  personBlock('person_remove', 'remove person', 'remove', COLOUR,
    "Admin only. Takes a person out of this workspace after the admin confirms: their sign-in is deleted and their " +
    "seat freed; everything they made keeps their name. They can be reinstated. Returns 'success', 'cancelled' or an error.");
  personBlock('person_reinstate', 'reinstate person', 'reinstate', COLOUR,
    "Admin only. Brings a removed person back onto the record they had, after the admin picks their role. People who " +
    "sign in by email get a link; people with a username get a password the admin sets. Returns 'success', 'cancelled' or an error.");
  personBlock('person_erase', 'erase person', 'erase', ERASE_COLOUR,
    "Admin only. Erases a person for good after the admin types their email to confirm: their details are cleared and " +
    "their sign-in deleted. Everything they made stays, with no name on it. Cannot be undone. Returns 'success', 'cancelled' or an error.");
  registerTransferBlock();
  registerDeleteAccountBlock();
}

/**
 * The person deletes their OWN account. No person input: the server
 * takes the subject from the token, never from the page. Not in the AI's vocabulary, as the other people
 * blocks are not.
 */
function registerDeleteAccountBlock(): void {
  (Blockly as any).Blocks['user_delete_account'] = {
    init: function () {
      this.appendDummyInput().appendField('delete my account');
      appendTheme(this);
      this.setInputsInline(false);
      this.setOutput(true, 'String');
      this.setColour(ERASE_COLOUR);
      this.setTooltip(
        "The signed-in person deletes their own account, after a warning and typing DELETE. They may be asked to " +
        "sign in again first. On success they are signed out and this device is cleared. Returns 'success', " +
        "'cancelled' or an error.");
      this.setHelpUrl('');
    },
  };
  (javascriptGenerator as any).forBlock['user_delete_account'] = function (block: any) {
    return [`await this.accountDeletion.deleteMyAccount(${themeCode(block)})`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}
